import { useContext } from 'react'
import { twMerge } from 'tailwind-merge'
import { Icons } from '../assets/icons'
import { StakeListContext } from '../context'
import { useInventory } from '../hooks/useInventory'

const ICONS: Record<string, (props: IconProps) => JSX.Element> = {
    ape: Icons.Ape,
    droid: Icons.Droid,
    lootbox: Icons.Lootbox,
    weapon: Icons.Weapon,
}

/**
 * Category navigation and the "what do I own" readout, as one tab bar.
 *
 * The page this replaces had four icon toggles that were simultaneously the stat display and a
 * multi-select type filter, distinguished only by a 20%-opacity off state. Single-select tabs with
 * an explicit ALL remove the ambiguity: the selected tab is obvious, and the counts are just the
 * tab labels rather than a second thing to read.
 */
export const CategoryTabs = () => {
    const { filter, setCategory } = useContext(StakeListContext)
    const { data, active, categories } = useInventory()

    return (
        // A horizontal scroll of five wide tabs clipped items mid-word and gave no hint that it
        // scrolled. They wrap into a grid until lg — the width at which all five fit on one row
        // without compressing — so every count stays visible at once, which is the whole point of
        // showing counts here.
        <div className='grid grid-cols-2 gap-2 lg:flex lg:flex-row lg:overflow-x-auto lg:no-scrollbar lg:snap-x'>
            <Tab
                className='col-span-2 lg:col-span-1'
                label='All items'
                count={data.length}
                activeCount={active.length}
                selected={filter.category === 'all'}
                onSelect={() => setCategory('all')}
            />
            {categories.map(({ value, label, count, active: activeCount }) => (
                <Tab
                    key={value}
                    Icon={ICONS[value]}
                    label={label}
                    count={count}
                    activeCount={activeCount}
                    selected={filter.category === value}
                    onSelect={() => setCategory(value)}
                />
            ))}
        </div>
    )
}

type TabProps = {
    Icon?: (props: IconProps) => JSX.Element
    label: string
    count: number
    activeCount: number
    selected: boolean
    onSelect: () => void
    className?: string
}

const Tab = ({ Icon, label, count, activeCount, selected, onSelect, className }: TabProps) => {
    return (
        <button
            onClick={onSelect}
            aria-pressed={selected}
            className={twMerge(
                'group flex flex-row items-center gap-2.5 md:gap-3 px-3 py-2.5 md:px-5 md:py-3 rounded text-left min-w-0',
                'lg:snap-start lg:shrink-0 border-b-2 transition-colors',
                selected
                    ? 'bg-[linear-gradient(90deg,rgba(3,206,255,0.2)_0%,rgba(201,31,255,0.2)_100%)] border-b-[#C91FFF]'
                    : 'bg-white/5 hover:bg-white/10 border-b-transparent',
                className
            )}
        >
            {Icon && (
                <Icon
                    className={twMerge('h-8 w-8 md:h-9 md:w-9 shrink-0', !selected && 'opacity-60 group-hover:opacity-100')}
                />
            )}
            {/* Tighter type on mobile: five tabs at the desktop scale took 222px of an 812px screen
                before a single item was visible. */}
            <span className='flex flex-col min-w-0 leading-tight'>
                {/* Stacked on a phone. Side by side, "250 Weapons" and "52 Loot Boxes" ran out of
                    room in a half-width tab and truncated to "250 WEAPO..." — the label being the
                    part that got cut, which is the part that identifies the tab. */}
                <span className='flex flex-col md:flex-row md:items-baseline md:gap-1.5'>
                    <span className='text-base md:text-xl font-black tabular-nums'>{count}</span>
                    <small className='font-bold uppercase text-xs md:text-sm md:truncate'>{label}</small>
                </span>
                <small className='text-white/50 font-bold uppercase whitespace-nowrap text-2xs md:text-xs'>
                    {activeCount} active
                </small>
            </span>
        </button>
    )
}
